import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown, User, UserPlus } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import Rentals from './Rentals';
import HelpLink from '../components/HelpLink';

/**
 * Rentals page. Single-surface active-rentals ledger. Leads moved to
 * Marketing → Lead Management (2026-06-05) so the prospecting funnel
 * lives alongside the channels that feed it.
 *
 * Routes:
 *   /rentals       -> Active rentals
 *
 * /leads in App.js now redirects to /reservations?tab=leads.
 */

const RentalsPage = () => {
  const { facilityId, loading: facilityLoading } = useFacility();
  const navigate = useNavigate();
  // New Rental splits by tenant kind (owner, 2026-08-10): existing tenant
  // (picked from the customer dropdown) or a brand-new tenant (created
  // inline on the rental form). Add-Existing-Tenant left the Customers
  // page for this menu.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  if (facilityLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">
          No facility selected
        </h2>
        <p className="text-[13px] text-slate-500 mt-1">
          Create a facility in Settings to manage rentals.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Rentals <HelpLink slug="move-in" /></h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            Active month-to-month rentals
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              New Rental
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {menuOpen && (
              /* left-anchored on phones: the stacked header puts the button
                 at the LEFT edge, so a right-anchored menu opened off-screen
                 (owner, 2026-08-11). sm+ keeps the desktop right-anchor. */
              <div className="absolute left-0 sm:left-auto sm:right-0 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg z-20 py-1">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate('/rentals/new?tenant=existing'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50"
                >
                  <User className="h-3.5 w-3.5 text-slate-400" />
                  Existing tenant
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate('/rentals/new?tenant=new'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50"
                >
                  <UserPlus className="h-3.5 w-3.5 text-slate-400" />
                  New tenant
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Rentals />
    </div>
  );
};

export default RentalsPage;
